import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { toEnglishDigits } from '@/core/format/numbers';
import { FieldShell } from './Field';
import { Icon } from './Icon';
import { Sheet } from './Sheet';
import { cn } from './cn';

/**
 * Normalises Persian/Arabic text so that searching is forgiving:
 * Arabic ي/ك → ی/ک, آ/أ/إ → ا, strips diacritics, ZWNJ and whitespace, unifies digits.
 */
export const normalizeFa = (s: string) =>
  toEnglishDigits(s)
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/ة/g, 'ه')
    .replace(/[آأإ]/g, 'ا')
    .replace(/[\u064B-\u065F\u0670\u200c\u200d\u200e\u200f\s]+/g, '')
    .toLowerCase();

function filterOptions(options: string[], query: string): string[] {
  const q = normalizeFa(query);
  if (!q) return options;
  const starts: string[] = [];
  const contains: string[] = [];
  for (const o of options) {
    const n = normalizeFa(o);
    if (n.startsWith(q)) starts.push(o);
    else if (n.includes(q)) contains.push(o);
  }
  return [...starts, ...contains];
}

interface ComboboxProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  emptyText?: string;
  error?: string | undefined;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  inputRef?: Ref<HTMLInputElement>;
}

const MAX_LIST_HEIGHT = 260;

function DesktopCombobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  emptyText = 'موردی یافت نشد',
  error,
  disabled,
  className,
  containerClassName,
  inputRef,
}: ComboboxProps) {
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputEl = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false); // filter only after the user actually typed
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number } | null>(null);

  // keep the text in sync when the value is changed from outside (swap button, presets…)
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setQuery(value);
    setTyped(false);
  }

  const visible = useMemo(() => (typed ? filterOptions(options, query) : options), [options, query, typed]);

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const flip = below < 180 && above > below;
    setPos(
      flip
        ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 6, maxHeight: Math.min(MAX_LIST_HEIGHT, above) }
        : { left: r.left, width: r.width, top: r.bottom + 6, maxHeight: Math.min(MAX_LIST_HEIGHT, below) },
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // scroll the highlighted option into view while navigating with the keyboard
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const close = () => {
    setOpen(false);
    setTyped(false);
  };

  const pick = (opt: string) => {
    setQuery(opt);
    setPrevValue(opt);
    onChange(opt);
    close();
  };

  const openList = () => {
    if (disabled) return;
    const i = options.indexOf(value);
    setActive(i >= 0 ? i : 0);
    setOpen(true);
  };

  const onBlur = () => {
    // typed text that exactly matches a station is accepted, anything else reverts to the last choice
    const exact = options.find((o) => normalizeFa(o) === normalizeFa(query) && query.trim() !== '');
    if (exact) {
      if (exact !== value) onChange(exact);
      setQuery(exact);
      setPrevValue(exact);
    } else {
      setQuery(value);
    }
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) return openList();
        setActive((i) => (visible.length ? (i + 1) % visible.length : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) return openList();
        setActive((i) => (visible.length ? (i - 1 + visible.length) % visible.length : 0));
        break;
      case 'Enter': {
        if (!open) return; // let the form submit
        e.preventDefault();
        const opt = visible[active];
        if (opt) pick(opt);
        break;
      }
      case 'Escape':
        if (open) {
          e.preventDefault();
          e.stopPropagation();
          setQuery(value);
          close();
        }
        break;
      case 'Tab':
        // select the highlighted option when the user tabs away after typing
        if (open && typed && visible[active]) pick(visible[active]);
        break;
    }
  };

  const activeId = open && visible[active] ? `${listId}-opt-${active}` : undefined;

  return (
    <FieldShell label={label} error={error} className={containerClassName}>
      {({ id, describedBy, invalid }) => (
        <div ref={wrapRef} className="relative">
          <input
            id={id}
            ref={(el) => {
              inputEl.current = el;
              if (typeof inputRef === 'function') inputRef(el);
              else if (inputRef) (inputRef as { current: HTMLInputElement | null }).current = el;
            }}
            type="text"
            role="combobox"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            disabled={disabled}
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setTyped(true);
              setActive(0);
              if (!open) setOpen(true);
            }}
            onFocus={(e) => {
              e.currentTarget.select();
              openList();
            }}
            onClick={() => !open && openList()}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={cn(
              'w-full rounded-md border bg-fill ps-4 pe-11 py-3 text-[15px] text-ink placeholder:text-ink-3 transition ' +
                'hover:border-turquoise/40 focus:border-turquoise focus:bg-paper focus:outline-none focus:ring-4 focus:ring-turquoise/15 disabled:opacity-60',
              invalid ? 'border-danger' : 'border-line',
              className,
            )}
          />
          <span className="pointer-events-none absolute inset-y-0 end-4 grid place-items-center text-ink-3">
            <Icon name="chevronDown" size={18} className={cn('transition-transform', open && 'rotate-180')} />
          </span>

          {open &&
            pos &&
            createPortal(
              <ul
                ref={listRef}
                id={listId}
                role="listbox"
                aria-label={label}
                dir="rtl"
                style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
                className="fixed z-[100] overflow-y-auto overscroll-contain rounded-md border border-line bg-paper p-1.5 shadow-floating"
                // keep focus in the input so blur doesn't fire before the click lands
                onMouseDown={(e) => e.preventDefault()}
              >
                {visible.length === 0 ? (
                  <li role="presentation" className="px-3 py-3 text-sm text-ink-3">
                    {emptyText}
                  </li>
                ) : (
                  visible.map((opt, i) => {
                    const selected = opt === value;
                    return (
                      <li
                        key={opt}
                        id={`${listId}-opt-${i}`}
                        data-index={i}
                        role="option"
                        aria-selected={selected}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => pick(opt)}
                        className={cn(
                          'flex cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2.5 text-[15px] text-ink',
                          i === active && 'bg-primary-light',
                          selected && 'font-bold text-action',
                        )}
                      >
                        <span>{opt}</span>
                        {selected && <Icon name="check" size={16} />}
                      </li>
                    );
                  })
                )}
              </ul>,
              document.body,
            )}
        </div>
      )}
    </FieldShell>
  );
}

/* ───────── Phone version: a bottom-sheet picker ───────── */

const PHONE_QUERY = '(max-width: 767px) and (pointer: coarse)';
const subscribePhone = (cb: () => void) => {
  if (typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(PHONE_QUERY);
  mq.addEventListener('change', cb);
  return () => mq.removeEventListener('change', cb);
};
const isPhone = () => typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches;

/** True on touch phones. False on the server and in tests, so those render the desktop combobox. */
function usePhone() {
  return useSyncExternalStore(subscribePhone, isPhone, () => false);
}

/**
 * On a phone a floating dropdown fights with the on-screen keyboard. Instead the field is a button that
 * opens a bottom sheet: all stations as large tap rows, and an optional search box (the keyboard only
 * appears if the person taps it).
 */
function PhoneCombobox({ label, value, onChange, options, placeholder, emptyText = 'موردی یافت نشد', error, disabled, className, containerClassName }: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const visible = useMemo(() => filterOptions(options, query), [options, query]);
  const showSearch = options.length > 8;

  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const pick = (opt: string) => {
    onChange(opt);
    close();
  };

  return (
    <FieldShell label={label} error={error} className={containerClassName}>
      {({ id, describedBy, invalid }) => (
        <>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={label ? `${label}: ${value || placeholder || ''}` : undefined}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            onClick={() => setOpen(true)}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-md border bg-fill ps-4 pe-4 py-3 text-start text-[15px] transition active:bg-primary-light disabled:opacity-60',
              invalid ? 'border-danger' : 'border-line',
              className,
            )}
          >
            <span className={cn('truncate', value ? 'text-ink' : 'text-ink-3')}>{value || placeholder}</span>
            <Icon name="chevronDown" size={18} className="shrink-0 text-ink-3" />
          </button>

          <Sheet open={open} onClose={close} title={label} initialFocus="sheet">
            {showSearch && (
              <div className="relative mb-3">
                <Icon name="search" size={18} className="pointer-events-none absolute inset-y-0 start-4 my-auto text-ink-3" />
                <input
                  type="search"
                  inputMode="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label="جستجوی ایستگاه"
                  placeholder="جستجوی نام شهر…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full rounded-md border border-line bg-fill py-3 pe-4 ps-11 text-ink placeholder:text-ink-3 focus:border-turquoise focus:bg-paper focus:outline-none focus:ring-4 focus:ring-turquoise/15"
                />
              </div>
            )}
            {visible.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-3">{emptyText}</p>
            ) : (
              <ul role="listbox" aria-label={label} className="-mx-2 max-h-[min(55dvh,26rem)] overflow-y-auto overscroll-contain">
                {visible.map((opt) => {
                  const selected = opt === value;
                  return (
                    <li key={opt} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => pick(opt)}
                        className={cn(
                          'flex min-h-[3.25rem] w-full items-center justify-between gap-2 rounded-md px-4 text-start text-base transition active:bg-primary-light',
                          selected ? 'bg-secondary-light/60 font-bold text-action' : 'text-ink',
                        )}
                      >
                        <span>{opt}</span>
                        {selected && <Icon name="check" size={20} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Sheet>
        </>
      )}
    </FieldShell>
  );
}

/** Autocomplete on desktop, bottom-sheet picker on phones. */
export function Combobox(props: ComboboxProps) {
  const phone = usePhone();
  return phone ? <PhoneCombobox {...props} /> : <DesktopCombobox {...props} />;
}
